'use client';

import React from 'react';
import type { RateBreakdown, RateInput } from '@/lib/rateCalculator';
import { formatCurrency } from '@/lib/rateCalculator';
import { TrendingUp, TrendingDown, Minus, Users, Zap, Star, ArrowRight } from 'lucide-react';
import Link from 'next/link';
import { useLanguage } from '@/context/LanguageContext';

interface RateBreakdownPanelProps {
  breakdown: RateBreakdown | null;
  input: RateInput;
  isCalculating: boolean;
}

function BreakdownRow({
  label,
  value,
  sublabel,
  highlight,
}: {
  label: string;
  value: string;
  sublabel?: string;
  highlight?: boolean;
}) {
  return (
    <div className={`flex items-center justify-between py-2.5 border-b border-border/50 last:border-0 ${highlight ? 'opacity-100' : 'opacity-90'}`}>
      <div>
        <div className="text-xs font-semibold text-muted-foreground uppercase tracking-wider">{label}</div>
        {sublabel && <div className="text-[10px] text-muted-foreground mt-0.5">{sublabel}</div>}
      </div>
      <div className={`text-sm font-bold font-tabular ${highlight ? 'text-accent' : 'text-foreground'}`}>
        {value}
      </div>
    </div>
  );
}

function RangeBar({ low, mid, high, labels }: { low: number; mid: number; high: number; labels: { floor: string; target: string; ceiling: string } }) {
  return (
    <div className="mt-4">
      <div className="flex items-center justify-between mb-2">
        <span className="text-xs font-semibold text-muted-foreground uppercase tracking-wider">
          Suggested Rate Range
        </span>
        <span className="text-[10px] text-muted-foreground">for negotiation</span>
      </div>
      <div className="flex gap-2">
        {[
          { label: labels.floor, value: low, color: 'bg-info/20 border-info/30 text-info' },
          { label: labels.target, value: mid, color: 'bg-primary/20 border-primary/30 text-primary' },
          { label: labels.ceiling, value: high, color: 'bg-accent/20 border-accent/30 text-accent' },
        ].map((item) => (
          <div key={`range-${item.label}`} className={`flex-1 rounded-lg border p-2.5 text-center ${item.color}`}>
            <div className="text-[9px] font-bold uppercase tracking-wider opacity-70 mb-1">{item.label}</div>
            <div className="text-sm font-extrabold font-tabular">{formatCurrency(item.value)}</div>
          </div>
        ))}
      </div>
    </div>
  );
}

export default function RateBreakdownPanel({ breakdown, input, isCalculating }: RateBreakdownPanelProps) {
  const { t } = useLanguage();

  const engagementStatusConfig = {
    excellent: { label: t.breakdown.excellent, color: 'text-positive', bg: 'bg-positive/10 border-positive/30', icon: TrendingUp },
    good: { label: t.breakdown.good, color: 'text-accent', bg: 'bg-accent/10 border-accent/30', icon: TrendingUp },
    average: { label: t.breakdown.average, color: 'text-info', bg: 'bg-info/10 border-info/30', icon: Minus },
    'below-average': { label: t.breakdown.belowAvg, color: 'text-danger', bg: 'bg-danger/10 border-danger/30', icon: TrendingDown },
  };

  const contentTypeLabels: Record<string, string> = {
    post: t.contentTypes.post,
    reel: t.contentTypes.reel,
    story: t.contentTypes.story,
    video: t.contentTypes.video,
    short: t.contentTypes.short,
  };

  if (isCalculating) {
    return (
      <div className="card-glow p-6 h-full">
        <div className="animate-pulse space-y-4">
          <div className="shimmer h-8 w-48 rounded-lg" />
          <div className="shimmer h-16 w-full rounded-xl" />
          <div className="shimmer h-4 w-full rounded" />
          <div className="shimmer h-4 w-3/4 rounded" />
          <div className="shimmer h-4 w-5/6 rounded" />
          <div className="shimmer h-4 w-2/3 rounded" />
          <div className="shimmer h-20 w-full rounded-xl" />
        </div>
      </div>
    );
  }

  if (!breakdown) {
    return (
      <div className="card-glow p-6 h-full flex flex-col items-center justify-center text-center gap-4">
        <div className="w-16 h-16 rounded-2xl bg-primary/10 border border-primary/20 flex items-center justify-center">
          <Zap size={28} className="text-primary" />
        </div>
        <div>
          <h3 className="text-lg font-bold text-foreground">{t.breakdown.title}</h3>
          <p className="text-sm text-muted-foreground mt-1 max-w-xs">{t.breakdown.subtitle}</p>
        </div>
        <div className="flex flex-col gap-2 w-full max-w-xs">
          {[t.breakdown.step1, t.breakdown.step2, t.breakdown.step3].map((step, i) => (
            <div key={`step-${i}`} className="flex items-center gap-3 text-left">
              <div className="w-6 h-6 rounded-full bg-primary/20 border border-primary/30 flex items-center justify-center flex-shrink-0">
                <span className="text-[10px] font-bold text-primary">{i + 1}</span>
              </div>
              <span className="text-xs text-muted-foreground">{step}</span>
            </div>
          ))}
        </div>
      </div>
    );
  }

  const engagementConfig = engagementStatusConfig[breakdown.engagementStatus];
  const EngagementIcon = engagementConfig.icon;
  const platformAvg = input.platform === 'instagram' ? '3.5' : input.platform === 'tiktok' ? '5.5' : '4.0';

  return (
    <div className="card-glow p-6 h-full flex flex-col gap-5 rate-reveal">
      {/* Hero Rate */}
      <div className="text-center py-4 rounded-xl border border-primary/30 bg-primary/10">
        <div className="text-xs font-bold uppercase tracking-widest text-primary/70 mb-1">
          {t.breakdown.recommended}
        </div>
        <div className="text-5xl font-extrabold font-tabular text-gradient-primary">
          {formatCurrency(breakdown.finalRate)}
        </div>
        <div className="text-xs text-muted-foreground mt-2">
          {contentTypeLabels[input.contentType]} {t.breakdown.on}{' '}
          {input.platform.charAt(0).toUpperCase() + input.platform.slice(1)} ·{' '}
          {t.niches[input.niche]}
        </div>
      </div>

      {/* Stats Row */}
      <div className="grid grid-cols-2 gap-3">
        <div className="rounded-lg bg-muted/30 border border-border p-3">
          <div className="flex items-center gap-1.5 mb-1">
            <Users size={12} className="text-muted-foreground" />
            <span className="text-[10px] font-semibold text-muted-foreground uppercase tracking-wider">
              {t.breakdown.audienceTier}
            </span>
          </div>
          <div className="text-sm font-bold text-foreground">{breakdown.audienceTier}</div>
        </div>
        <div className={`rounded-lg border p-3 ${engagementConfig.bg}`}>
          <div className="flex items-center gap-1.5 mb-1">
            <EngagementIcon size={12} className={engagementConfig.color} />
            <span className="text-[10px] font-semibold text-muted-foreground uppercase tracking-wider">
              {t.breakdown.engagement}
            </span>
          </div>
          <div className={`text-sm font-bold ${engagementConfig.color}`}>{engagementConfig.label}</div>
        </div>
      </div>

      {/* Estimated Reach */}
      <div className="flex items-center justify-between px-3 py-2 rounded-lg bg-muted/20 border border-border">
        <span className="text-xs text-muted-foreground font-medium">{t.breakdown.estimatedReach}</span>
        <span className="text-sm font-bold font-tabular text-foreground">
          ~{breakdown.estimatedReach.toLocaleString('en-US')} {t.breakdown.engagements}
        </span>
      </div>

      {/* Breakdown */}
      <div className="rounded-xl border border-border/60 bg-muted/10 px-4 py-1">
        <BreakdownRow
          label={t.breakdown.baseRate}
          value={`$${breakdown.baseRate.toLocaleString('en-US')}`}
          sublabel={`$${breakdown.baseCPM} CPM × ${(input.followers / 1000).toFixed(1)}K followers`}
        />
        <BreakdownRow
          label={t.breakdown.engagementMultiplier}
          value={`${breakdown.engagementMultiplier}×`}
          sublabel={`${input.engagementRate}% vs platform avg ${platformAvg}%`}
        />
        <BreakdownRow
          label={t.breakdown.contentTypeLabel}
          value={`${breakdown.contentTypeMultiplier}×`}
          sublabel={contentTypeLabels[input.contentType]}
        />
        <BreakdownRow
          label={t.breakdown.nichePremium}
          value={`+${Math.round(breakdown.nichePremium * 100)}%`}
          sublabel={t.niches[input.niche]}
          highlight
        />
        {breakdown.usageRightsAddon > 0 && (
          <BreakdownRow
            label={t.breakdown.usageRights}
            value="+25%"
            sublabel={t.breakdown.usageRightsDesc}
          />
        )}
        {breakdown.exclusivityAddon > 0 && (
          <BreakdownRow
            label={t.breakdown.exclusivity}
            value="+30%"
            sublabel={t.breakdown.exclusivityDesc}
          />
        )}
      </div>

      {/* Range */}
      <RangeBar
        low={breakdown.suggestedLow}
        mid={breakdown.suggestedMid}
        high={breakdown.suggestedHigh}
        labels={{ floor: t.breakdown.floor, target: t.breakdown.target, ceiling: t.breakdown.ceiling }}
      />

      {/* CTA */}
      <Link href="/rate-card" className="btn-accent flex items-center justify-center gap-2 w-full py-3 text-sm">
        <Star size={15} />
        {t.breakdown.generateCard}
        <ArrowRight size={15} />
      </Link>

      {breakdown.engagementStatus === 'below-average' && (
        <div className="flex items-start gap-2 p-3 rounded-lg bg-danger/10 border border-danger/30">
          <TrendingDown size={14} className="text-danger flex-shrink-0 mt-0.5" />
          <p className="text-xs text-danger/90">{t.breakdown.belowAvgWarning}</p>
        </div>
      )}
    </div>
  );
}